import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';
export interface WeatherForecast {
  wellId: string;
  lat: number;
  lon: number;
  current: {
    temp: number;
    feelsLike: number;
    humidity: number;
    windSpeed: number;
    windGust?: number;
    weather: {
      main: string;
      description: string;
      icon: string;
    };
  };
  forecast: DailyForecast[];
  alerts: WeatherAlert[];
  operationalRisk: 'low' | 'medium' | 'high' | 'critical';

  unavailable?: boolean;
  unavailableMessage?: string;
}

export interface DailyForecast {
  date: string;
  tempHigh: number;
  tempLow: number;
  precipitation: number;
  precipProbability: number;
  windSpeed: number;
  windGust?: number;
  weather: {
    main: string;
    description: string;
    icon: string;
  };
}

export interface WeatherAlert {
  event: string;
  severity: 'minor' | 'moderate' | 'severe' | 'extreme';
  description: string;
  start: number;
  end: number;
}

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  private baseUrl = `${environment.apiConfig.uri}api/weather`;

  constructor(private http: HttpClient) {}

  /**
   * Fetch weather for multiple well locations
   */
  getWeatherForWells(wells: { wellId: string, lat: number, lon: number }[]): Observable<WeatherForecast[]> {
    // Deduplicate nearby wells (within ~5 miles) to reduce API calls
    const { representatives, clusterMap } = this.deduplicateLocations(wells);

    const requests = representatives.map(well =>
      this.getWeatherForLocation(well.wellId, well.lat, well.lon)
    );

    return forkJoin(requests).pipe(
      map(forecasts => {
        // Map each representative's weather back to all wells in its cluster
        const allForecasts: WeatherForecast[] = [];
        for (const forecast of forecasts) {
          allForecasts.push(forecast);
          const nearbyWellIds = clusterMap.get(forecast.wellId) || [];
          for (const nearbyWellId of nearbyWellIds) {
            allForecasts.push({ ...forecast, wellId: nearbyWellId });
          }
        }
        return allForecasts;
      })
    );
  }

  /**
   * Get weather for a single location
   */
  private getWeatherForLocation(wellId: string, lat: number, lon: number): Observable<WeatherForecast> {
    const url = `${this.baseUrl}/forecast?lat=${lat}&lon=${lon}`;

    return this.http.get<{ current: any, forecast: any }>(url).pipe(
      map(({ current, forecast }) => this.transformFreeAPIData(wellId, lat, lon, current, forecast)),
        catchError(error => {
          console.error(`❌ Weather fetch failed for well ${wellId}:`, error);

          const isQuota = this.isWeatherQuotaOrTrialError(error);

          if (isQuota) {
            console.warn('⚠️ Weather API quota/trial limit hit. Falling back to unavailable forecast.');
            return of(this.getWeatherUnavailableForecast(wellId, lat, lon));
          }

          // keep existing fallback for random errors
          return of(this.getEmptyForecast(wellId, lat, lon));
        })
    );
  }

  /**
 * Transform Free API response (current + forecast) to our format
 */
  private transformFreeAPIData(wellId: string, lat: number, lon: number, current: any, forecastData: any): WeatherForecast {
    // Current weather
    const currentWeather = {
      temp: Math.round(current.main.temp),
      feelsLike: Math.round(current.main.feels_like),
      humidity: current.main.humidity,
      windSpeed: Math.round(current.wind.speed),
      windGust: current.wind.gust ? Math.round(current.wind.gust) : undefined,
      weather: {
        main: current.weather[0].main,
        description: current.weather[0].description,
        icon: current.weather[0].icon
      }
    };

    // Process 5-day forecast (3-hour intervals) into daily summaries
    const dailyForecasts = this.aggregateForecastByDay(forecastData.list);

    // Check for alerts (free tier doesn't have alerts, so we'll simulate based on conditions)
    const alerts: WeatherAlert[] = [];

    // Calculate operational risk
    const operationalRisk = this.calculateOperationalRisk(dailyForecasts, alerts);

    return {
      wellId,
      lat,
      lon,
      current: currentWeather,
      forecast: dailyForecasts,
      alerts,
      operationalRisk
    };
  }

  /**
   * Aggregate 3-hour forecast intervals into daily summaries
   */
  private aggregateForecastByDay(forecastList: any[]): DailyForecast[] {
    const dailyMap = new Map<string, any[]>();

    // Group forecasts by date
    forecastList.forEach(item => {
      const date = new Date(item.dt * 1000).toISOString().split('T')[0];
      if (!dailyMap.has(date)) {
        dailyMap.set(date, []);
      }
      dailyMap.get(date)!.push(item);
    });

    // Convert to daily summaries (take up to 7 days)
    const dailyForecasts: DailyForecast[] = [];
    let count = 0;

    for (const [date, items] of dailyMap.entries()) {
      if (count >= 7) break;

      const temps = items.map(i => i.main.temp);
      const winds = items.map(i => i.wind.speed);
      const precip = items.reduce((sum, i) => sum + (i.rain?.['3h'] || 0) + (i.snow?.['3h'] || 0), 0);
      const popValues = items.map(i => i.pop || 0);

      // Find the icon that appears most during daylight hours
      const dayItems = items.filter(i => {
        const hour = new Date(i.dt * 1000).getHours();
        return hour >= 6 && hour <= 18;
      });
      const representativeWeather = dayItems.length > 0
        ? dayItems[Math.floor(dayItems.length / 2)].weather[0]
        : items[0].weather[0];

      dailyForecasts.push({
        date,
        tempHigh: Math.round(Math.max(...temps)),
        tempLow: Math.round(Math.min(...temps)),
        precipitation: precip,
        precipProbability: Math.round(Math.max(...popValues) * 100),
        windSpeed: Math.round(Math.max(...winds)),
        windGust: items.some(i => i.wind.gust)
          ? Math.round(Math.max(...items.map(i => i.wind.gust || 0)))
          : undefined,
        weather: {
          main: representativeWeather.main,
          description: representativeWeather.description,
          icon: representativeWeather.icon
        }
      });

      count++;
    }

    return dailyForecasts;
  }


  /**
   * Transform OpenWeatherMap response to our format
   */
  private transformWeatherData(wellId: string, lat: number, lon: number, data: any): WeatherForecast {
    const current = data.current;
    const daily = data.daily.slice(0, 7); // Next 7 days
    const alerts = data.alerts || [];

    const forecast: DailyForecast[] = daily.map((day: any) => ({
      date: new Date(day.dt * 1000).toISOString().split('T')[0],
      tempHigh: Math.round(day.temp.max),
      tempLow: Math.round(day.temp.min),
      precipitation: (day.rain || 0) + (day.snow || 0),
      precipProbability: Math.round((day.pop || 0) * 100),
      windSpeed: Math.round(day.wind_speed),
      windGust: day.wind_gust ? Math.round(day.wind_gust) : undefined,
      weather: {
        main: day.weather[0].main,
        description: day.weather[0].description,
        icon: day.weather[0].icon
      }
    }));

    const transformedAlerts: WeatherAlert[] = alerts.map((alert: any) => ({
      event: alert.event,
      severity: this.mapSeverity(alert.tags),
      description: alert.description,
      start: alert.start,
      end: alert.end
    }));

    const operationalRisk = this.calculateOperationalRisk(forecast, transformedAlerts);

    return {
      wellId,
      lat,
      lon,
      current: {
        temp: Math.round(current.temp),
        feelsLike: Math.round(current.feels_like),
        humidity: current.humidity,
        windSpeed: Math.round(current.wind_speed),
        windGust: current.wind_gust ? Math.round(current.wind_gust) : undefined,
        weather: {
          main: current.weather[0].main,
          description: current.weather[0].description,
          icon: current.weather[0].icon
        }
      },
      forecast,
      alerts: transformedAlerts,
      operationalRisk
    };
  }

  /**
   * Calculate operational risk based on weather conditions
   */
  private calculateOperationalRisk(
    forecast: DailyForecast[], 
    alerts: WeatherAlert[]
  ): 'low' | 'medium' | 'high' | 'critical' {
    // Critical: Severe alerts or extreme conditions
    if (alerts.some(a => a.severity === 'extreme' || a.severity === 'severe')) {
      return 'critical';
    }

    // Check next 7 days for hazardous conditions
    const hazardousConditions = forecast.some(day => 
      day.tempLow < 20 ||           // Hard freeze
      day.tempHigh > 105 ||         // Extreme heat
      day.windGust && day.windGust > 40 || // High winds
      day.precipitation > 2 ||      // Heavy precip
      day.precipProbability > 80    // Very likely precip
    );

    if (hazardousConditions) return 'high';

    // Medium: Moderate alerts or challenging conditions
    if (alerts.length > 0 || forecast.some(day => 
      day.tempLow < 32 ||           // Freezing
      day.windSpeed > 25 ||         // Moderate winds
      day.precipitation > 0.5       // Moderate precip
    )) {
      return 'medium';
    }

    return 'low';
  }

  /**
   * Deduplicate wells within ~5 miles (0.08 degrees ≈ 5 miles).
   * Returns the representative wells and a map of representative wellId -> list of nearby wellIds.
   */
  private deduplicateLocations(
    wells: { wellId: string, lat: number, lon: number }[]
  ): { representatives: { wellId: string, lat: number, lon: number }[], clusterMap: Map<string, string[]> } {
    const threshold = 0.08;
    const representatives: { wellId: string, lat: number, lon: number }[] = [];
    const clusterMap = new Map<string, string[]>();

    for (const well of wells) {
      const existing = representatives.find(rep =>
        Math.abs(rep.lat - well.lat) < threshold &&
        Math.abs(rep.lon - well.lon) < threshold
      );

      if (existing) {
        // Add this well to the existing representative's cluster
        if (!clusterMap.has(existing.wellId)) {
          clusterMap.set(existing.wellId, []);
        }
        clusterMap.get(existing.wellId)!.push(well.wellId);
      } else {
        representatives.push(well);
      }
    }

    return { representatives, clusterMap };
  }

  /**
   * Map OpenWeatherMap severity tags to our levels
   */
  private mapSeverity(tags: string[]): 'minor' | 'moderate' | 'severe' | 'extreme' {
    if (!tags || tags.length === 0) return 'minor';
    
    const tagStr = tags.join(',').toLowerCase();
    if (tagStr.includes('extreme')) return 'extreme';
    if (tagStr.includes('severe')) return 'severe';
    if (tagStr.includes('moderate')) return 'moderate';
    return 'minor';
  }

  /**
   * Return empty forecast if API fails
   */
  private getEmptyForecast(wellId: string, lat: number, lon: number): WeatherForecast {
    return {
      wellId,
      lat,
      lon,
      current: {
        temp: 0,
        feelsLike: 0,
        humidity: 0,
        windSpeed: 0,
        weather: { main: 'Unknown', description: 'Data unavailable', icon: '01d' }
      },
      forecast: [],
      alerts: [],
      operationalRisk: 'low'
    };
  }

  /**
   * Get weather icon URL from OpenWeatherMap
   */
  getWeatherIconUrl(icon: string): string {
    return `https://openweathermap.org/img/wn/${icon}@2x.png`;
  }

  /**
   * Get risk color for UI
   */
  getRiskColor(risk: 'low' | 'medium' | 'high' | 'critical'): string {
    const colors = {
      low: '#04AC66',      // Green
      medium: '#ffae00',   // Yellow
      high: '#ff6600',     // Orange
      critical: '#e63946'  // Red
    };
    return colors[risk];
  }

  /**
   * Get risk description
   */
  getRiskDescription(risk: 'low' | 'medium' | 'high' | 'critical'): string {
    const descriptions = {
      low: 'Favorable conditions',
      medium: 'Monitor conditions',
      high: 'Challenging conditions',
      critical: 'Operations at risk'
    };
    return descriptions[risk];
  }

  private isWeatherQuotaOrTrialError(error: any): boolean {
    const status = error?.status;
    const msg = (error?.error?.message || error?.message || '').toString().toLowerCase();

    // Common cases:
    // - 429: too many requests / rate limit
    // - 401: invalid API key (or key restricted)
    // - 403: forbidden (plan/trial restrictions)
    if (status === 429 || status === 401 || status === 403) return true;

    // Sometimes OpenWeather returns helpful text in message bodies
    return (
      msg.includes('rate limit') ||
      msg.includes('too many') ||
      msg.includes('quota') ||
      msg.includes('exceeded') ||
      msg.includes('trial') ||
      msg.includes('subscription') ||
      msg.includes('invalid api key') ||
      msg.includes('not authorized')
    );
  }

  private getWeatherUnavailableForecast(wellId: string, lat: number, lon: number): WeatherForecast {
    return {
      wellId,
      lat,
      lon,
      current: {
        temp: 0,
        feelsLike: 0,
        humidity: 0,
        windSpeed: 0,
        weather: { main: 'Unavailable', description: 'Weather temporarily unavailable', icon: '01d' }
      },
      forecast: [],
      alerts: [],
      operationalRisk: 'low',
      unavailable: true,
      unavailableMessage: 'Weather is temporarily unavailable right now (beta/trial limits).'
    };
  }


}
